import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { fmtDate } from "@/lib/time";
import { Plus, Search, Edit2, Trash2, ChevronLeft, ChevronRight, Handshake, X, CheckCircle2, DollarSign, Clock, Mail, Send, Copy, ExternalLink, ShieldAlert } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  active:   "bg-accent/10 text-accent",
  inactive: "bg-muted text-muted-foreground",
  pending:  "bg-amber-500/10 text-amber-600 dark:text-amber-400",
};

const COMM_BADGE = {
  pending:  "bg-amber-500/10 text-amber-600",
  approved: "bg-blue-500/10 text-blue-600",
  paid:     "bg-green-500/10 text-green-600",
  rejected: "bg-destructive/10 text-destructive",
};

const inp = "w-full bg-background border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-ring transition-colors";

const DEFAULT_TIERS = [
  { name: "Bronze",   color: "#CD7F32", commission_pct: 5,  min_referrals: 0  },
  { name: "Silver",   color: "#A8A9AD", commission_pct: 8,  min_referrals: 5  },
  { name: "Gold",     color: "#FFD700", commission_pct: 12, min_referrals: 10 },
  { name: "Platinum", color: "#B9F2FF", commission_pct: 15, min_referrals: 20 },
];

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</label>
      {children}
    </div>
  );
}

function CredentialsModal({ data, onClose }) {
  if (!data) return null;
  const copyText = (text) => { navigator.clipboard?.writeText(text); toast.success("Copied!"); };
  const copyAll = () => {
    const text = `Law OS Partner Portal\nLogin URL: ${data.login_url}\nEmail: ${data.email}\nPassword: ${data.tmp_password}`;
    navigator.clipboard?.writeText(text);
    toast.success("All credentials copied!");
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-border">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-xs font-medium text-green-600 uppercase tracking-wider">
                {data.resent ? "Credentials Re-dispatched" : "Portal Access Granted"}
              </span>
            </div>
            <h3 className="font-semibold text-foreground">{data.name}</h3>
            <p className="text-xs text-muted-foreground">{data.email}</p>
          </div>
          <button onClick={onClose} data-testid="creds-modal-close" className="p-1.5 rounded-lg hover:bg-muted transition-colors">
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Credentials */}
        <div className="p-6 space-y-4">
          <div className="space-y-3">
            {/* Login URL */}
            <div className="flex items-center justify-between gap-3 px-4 py-3 bg-muted rounded-xl border border-border">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Login URL</div>
                <a href={data.login_url} target="_blank" rel="noreferrer"
                  className="text-xs text-primary hover:underline flex items-center gap-1 truncate">
                  {data.login_url} <ExternalLink className="w-3 h-3 shrink-0" />
                </a>
              </div>
              <button onClick={() => copyText(data.login_url)} className="shrink-0 p-1.5 rounded-lg hover:bg-background transition-colors" title="Copy URL">
                <Copy className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            </div>

            {/* Email */}
            <div className="flex items-center justify-between gap-3 px-4 py-3 bg-muted rounded-xl border border-border">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Email</div>
                <span className="text-sm font-mono text-foreground">{data.email}</span>
              </div>
              <button onClick={() => copyText(data.email)} className="shrink-0 p-1.5 rounded-lg hover:bg-background transition-colors" title="Copy email">
                <Copy className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            </div>

            {/* Temp Password */}
            <div className="flex items-center justify-between gap-3 px-4 py-3 bg-primary/5 border border-primary/20 rounded-xl">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-primary/70 mb-0.5">Temporary Password</div>
                <span className="text-sm font-mono font-semibold text-foreground tracking-widest">{data.tmp_password}</span>
                <div className="text-[10px] text-muted-foreground mt-0.5">Partner must change on first login</div>
              </div>
              <button onClick={() => copyText(data.tmp_password)} data-testid="copy-tmp-password"
                className="shrink-0 p-1.5 rounded-lg hover:bg-primary/10 transition-colors" title="Copy password">
                <Copy className="w-3.5 h-3.5 text-primary" />
              </button>
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <button onClick={copyAll} data-testid="copy-all-creds"
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
              <Copy className="w-3.5 h-3.5" /> Copy All
            </button>
            <button onClick={onClose} data-testid="creds-modal-done"
              className="flex-1 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors">
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SMPartners() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [tab, setTab] = useState("Partners");
  const [partners, setPartners] = useState([]);
  const [tiers, setTiers]       = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [pendingKyc, setPendingKyc] = useState([]);
  const [pendingKycLoading, setPendingKycLoading] = useState(false);
  const [selectedKyc, setSelectedKyc]   = useState(new Set());   // bulk select
  const [bulkApproving, setBulkApproving] = useState(false);
  const [commTotal, setCommTotal] = useState(0);
  const [commPage, setCommPage] = useState(1);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [search, setSearch]     = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [commStatusFilter, setCommStatusFilter] = useState("");
  const [loading, setLoading]   = useState(true);
  const [commLoading, setCommLoading] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing]   = useState(null);
  const blank = { name: "", email: "", phone: "", company: "", tier_id: "", region: "", status: "active", notes: "" };
  const [form, setForm] = useState(blank);
  // New commission form
  const [commSheetOpen, setCommSheetOpen] = useState(false);
  const [commForm, setCommForm] = useState({ partner_id: "", amount: "", description: "", status: "pending" });
  const [commSaving, setCommSaving] = useState(false);
  const [credsModal, setCredsModal] = useState(null); // { name, email, tmp_password, login_url }
  const [dispatchingId, setDispatchingId] = useState(null);

  const loadTiers = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/sm/partners/tiers`, { withCredentials: true }); setTiers(r.data); } catch {}
  }, []);

  const loadPartners = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.q = search;
      if (statusFilter) params.status = statusFilter;
      const r = await axios.get(`${API}/api/sm/partners`, { params, withCredentials: true });
      setPartners(r.data.items); setTotal(r.data.total);
    } catch { toast.error("Failed to load partners"); }
    finally { setLoading(false); }
  }, [page, search, statusFilter]);

  const loadCommissions = useCallback(async () => {
    setCommLoading(true);
    try {
      const params = { page: commPage, limit: 20 };
      if (commStatusFilter) params.status = commStatusFilter;
      const r = await axios.get(`${API}/api/sm/partners/commissions`, { params, withCredentials: true });
      setCommissions(r.data.items); setCommTotal(r.data.total);
    } catch { toast.error("Failed to load commissions"); }
    finally { setCommLoading(false); }
  }, [commPage, commStatusFilter]);

  useEffect(() => { loadTiers(); }, [loadTiers]);
  useEffect(() => { loadPartners(); }, [loadPartners]);
  useEffect(() => { if (tab === "Commissions") loadCommissions(); }, [tab, loadCommissions]);
  useEffect(() => { if (tab === "Pending KYC") loadPendingKyc(); }, [tab]);
  useEffect(() => {
    if (window.location.search.includes("new=1")) { openCreate(); window.history.replaceState({}, "", window.location.pathname); }
  }, []);

  const openCreate = () => { setEditing(null); setForm(blank); setSheetOpen(true); };
  const openEdit   = (p) => { setEditing(p); setForm({ name: p.name, email: p.email, phone: p.phone||"", company: p.company||"", tier_id: p.tier_id||"", region: p.region||"", status: p.status, notes: p.notes||"" }); setSheetOpen(true); };

  const handleSubmit = async () => {
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    try {
      if (editing) {
        await axios.patch(`${API}/api/sm/partners/${editing.id}`, form, { withCredentials: true });
        toast.success("Partner updated");
      } else {
        await axios.post(`${API}/api/sm/partners`, form, { withCredentials: true });
        toast.success("Partner added");
      }
      setSheetOpen(false); loadPartners();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : Array.isArray(d) ? d.map(x => x.msg).join(", ") : "Error saving partner");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Archive this partner? They will be marked as deleted but records are kept.")) return;
    try {
      await axios.delete(`${API}/api/sm/partners/${id}`, { withCredentials: true });
      toast.success("Partner archived");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to archive");
    }
    loadPartners();
  };

  const handlePurge = async (id, name) => {
    if (!window.confirm(`PERMANENTLY DELETE "${name}" and all related records? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/sm/partners/${id}/purge`, { withCredentials: true });
      toast.success("Partner permanently deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to purge");
    }
    loadPartners();
  };

  const handleResendCredentials = async (e, id, name, email) => {
    e.stopPropagation();
    setDispatchingId(id);
    try {
      const r = await axios.post(`${API}/api/sm/partners/${id}/resend-credentials`, {}, { withCredentials: true });
      const loginUrl = `${window.location.origin}/partner/login`;
      setCredsModal({ name, email, tmp_password: r.data.tmp_password, login_url: loginUrl, resent: true });
      if (!r.data.email_sent) toast.warning("Email delivery failed — share credentials manually");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to dispatch credentials");
    } finally { setDispatchingId(null); }
  };

  const handleDispatchNewPartner = async (e, id, name, email) => {
    e.stopPropagation();
    setDispatchingId(id);
    try {
      const r = await axios.post(`${API}/api/sm/partners/${id}/kyc-approve`, {}, { withCredentials: true });
      const loginUrl = `${window.location.origin}/partner/login`;
      setCredsModal({ name, email, tmp_password: r.data.tmp_password, login_url: loginUrl, resent: false });
      if (!r.data.email_sent) toast.warning("Email delivery failed — share credentials manually");
      loadPendingKyc(); loadPartners();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Approval failed");
    } finally { setDispatchingId(null); }
  };

  const seedTiers = async () => {
    for (const t of DEFAULT_TIERS) await axios.post(`${API}/api/sm/partners/tiers`, t, { withCredentials: true }).catch(() => {});
    loadTiers(); toast.success("Default tiers created");
  };

  const saveCommission = async () => {
    if (!commForm.partner_id || !commForm.amount) { toast.error("Partner and amount are required"); return; }
    setCommSaving(true);
    try {
      await axios.post(`${API}/api/sm/partners/commissions`, {
        ...commForm, amount: Number(commForm.amount)
      }, { withCredentials: true });
      toast.success("Commission created"); setCommSheetOpen(false); loadCommissions();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create commission"); }
    finally { setCommSaving(false); }
  };

  const approveCommission = async (id) => {
    try {
      const r = await axios.post(`${API}/api/sm/partners/commissions/${id}/approve`, {}, { withCredentials: true });
      toast.success(r.data.gl_posted
        ? `Approved + GL posted: ${r.data.journal}`
        : "Approved (GL posting skipped — account not found)");
      loadCommissions();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to approve"); }
  };

  const markPaidCommission = async (id) => {
    try {
      const r = await axios.post(`${API}/api/sm/partners/commissions/${id}/mark-paid`, {}, { withCredentials: true });
      toast.success(r.data.gl_posted
        ? `Paid + GL posted: ${r.data.journal}`
        : "Paid (GL posting skipped — account not found)");
      loadCommissions();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to mark paid"); }
  };

  const loadPendingKyc = async () => {
    setPendingKycLoading(true);
    try {
      const r = await axios.get(`${API}/api/sm/partners?status=kyc_pending&limit=50`, { withCredentials: true });
      setPendingKyc(r.data.items || []);
    } catch { toast.error("Failed to load pending KYC"); }
    finally { setPendingKycLoading(false); }
  };

  const approveKyc = async (id, name, email) => {
    if (!window.confirm(`Approve KYC for ${name} and dispatch portal credentials?`)) return;
    setDispatchingId(id);
    try {
      const r = await axios.post(`${API}/api/sm/partners/${id}/kyc-approve`, {}, { withCredentials: true });
      const loginUrl = `${window.location.origin}/partner/login`;
      setCredsModal({ name, email, tmp_password: r.data.tmp_password, login_url: loginUrl, resent: false });
      if (!r.data.email_sent) toast.warning("Email delivery failed — share credentials manually via the panel below");
      loadPendingKyc(); loadPartners();
    } catch (e) { toast.error(e.response?.data?.detail || "KYC approval failed"); }
    finally { setDispatchingId(null); }
  };

  const bulkApprove = async () => {
    if (selectedKyc.size === 0) return;
    if (!window.confirm(`Approve ${selectedKyc.size} partner(s) and dispatch credentials to all?`)) return;
    setBulkApproving(true);
    let passed = 0, failed = 0;
    for (const id of selectedKyc) {
      try {
        await axios.post(`${API}/api/sm/partners/${id}/kyc-approve`, {}, { withCredentials: true });
        passed++;
      } catch { failed++; }
    }
    setBulkApproving(false);
    setSelectedKyc(new Set());
    if (failed === 0) toast.success(`${passed} partner(s) approved and credentials sent.`);
    else toast.warning(`${passed} approved, ${failed} failed. Check individual rows.`);
    loadPendingKyc(); loadPartners();
  };

  const tierColor = (id) => tiers.find(t => t.id === id)?.color || "";
  const tierName  = (id) => tiers.find(t => t.id === id)?.name  || "";
  const pages = Math.ceil(total / 20);
  const commPages = Math.ceil(commTotal / 20);

  return (
    <div className="space-y-6">
      {/* Credentials Dispatch Modal */}
      <CredentialsModal data={credsModal} onClose={() => setCredsModal(null)} />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <div className="overline mb-1">Channel</div>
          <h1 className="font-serif text-4xl font-light">Partners.</h1>
          <p className="text-muted-foreground text-sm mt-1">{total} partner{total !== 1 ? "s" : ""} registered</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap sm:shrink-0">
          {tiers.length === 0 && (
            <button onClick={seedTiers} className="flex-1 sm:flex-none px-4 py-2 rounded-full border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
              Setup Tiers
            </button>
          )}
          {tab === "Partners" && (
            <button onClick={openCreate} data-testid="add-partner-btn"
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors">
              <Plus className="w-4 h-4" /> Add Partner
            </button>
          )}
          {tab === "Commissions" && (
            <button onClick={() => { setCommForm({ partner_id: "", amount: "", description: "", status: "pending" }); setCommSheetOpen(true); }} data-testid="add-commission-btn"
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors">
              <Plus className="w-4 h-4" /> Log Commission
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-0.5 border-b border-border">
        {["Partners", "Commissions", "Pending KYC"].map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`partners-tab-${t.toLowerCase().replace(" ", "-")}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors relative ${tab === t ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {t}
            {t === "Pending KYC" && pendingKyc.length > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-[10px] font-bold">{pendingKyc.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tier chips */}
      {tab === "Partners" && tiers.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {tiers.map(t => (
            <span key={t.id} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-border bg-muted text-xs text-muted-foreground">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: t.color }} />
              {t.name} · {t.commission_pct}%
            </span>
          ))}
        </div>
      )}

      {/* ── Partners Tab ─────────────────────────────────────────── */}
      {tab === "Partners" && (
        <>
          {/* Filters */}
          <div className="flex gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search name, email, company…" className={`${inp} pl-9`} data-testid="partner-search" />
            </div>
            <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
              className={`${inp} max-w-[150px]`} data-testid="partner-status-filter">
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          <div className="bg-card border border-border rounded-2xl overflow-hidden" data-testid="partners-table">
            {loading ? (
              <div className="flex items-center justify-center h-40">
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : partners.length === 0 ? (
              <div className="flex flex-col items-center py-14 gap-2 text-muted-foreground">
                <Handshake className="w-9 h-9 opacity-25" />
                <p className="text-sm">No partners yet.</p>
                <button onClick={openCreate} className="text-xs text-primary hover:underline">Add first partner →</button>
              </div>
            ) : (
              <>
                {/* ── Mobile card view (sm and below) ── */}
                <div className="md:hidden divide-y divide-border">
                  {partners.map(p => (
                    <div key={p.id} className="p-4 flex items-start justify-between gap-3" data-testid={`partner-card-${p.id}`}>
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary shrink-0 mt-0.5">
                          {p.name?.[0]?.toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground">{p.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{p.email}</p>
                          {p.phone && <p className="text-xs text-muted-foreground">{p.phone}</p>}
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full capitalize ${STATUS_BADGE[p.status] || "bg-muted text-muted-foreground"}`}>
                              {p.status}
                            </span>
                            {p.tier_name && (
                              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: tierColor(p.tier_id) }} />
                                {p.tier_name}
                              </span>
                            )}
                            {p.portal_access && (
                              <span className="text-[11px] text-emerald-600 font-medium">Portal active</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 shrink-0">
                        <button
                          onClick={() => openEdit(p)}
                          data-testid={`edit-partner-mobile-${p.id}`}
                          className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-all"
                        >
                          <Edit2 className="w-3 h-3" /> Edit
                        </button>
                        {p.portal_access ? (
                          <button onClick={e => { e.stopPropagation(); handleResendCredentials(e, p.id, p.name, p.email); }}
                            data-testid={`resend-creds-mobile-${p.id}`}
                            disabled={dispatchingId === p.id}
                            className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border border-primary/30 text-primary hover:bg-primary/10 transition-all disabled:opacity-50">
                            <Send className="w-3 h-3" />
                            {dispatchingId === p.id ? "…" : "Creds"}
                          </button>
                        ) : (
                          <button onClick={e => { e.stopPropagation(); handleDispatchNewPartner(e, p.id, p.name, p.email); }}
                            data-testid={`issue-creds-mobile-${p.id}`}
                            disabled={dispatchingId === p.id}
                            className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border border-amber-500/40 text-amber-600 hover:bg-amber-500/10 transition-all disabled:opacity-50">
                            <Send className="w-3 h-3" />
                            {dispatchingId === p.id ? "…" : "Issue"}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* ── Desktop table view (md and above) ── */}
                <table className="w-full hidden md:table">
                  <thead>
                    <tr className="border-b border-border bg-muted/40">
                      <th className="text-left overline px-5 py-3.5">Name</th>
                      <th className="text-left overline px-5 py-3.5 hidden lg:table-cell">Company</th>
                      <th className="text-left overline px-5 py-3.5 hidden xl:table-cell">Tier</th>
                      <th className="text-left overline px-5 py-3.5">Status</th>
                      <th className="px-5 py-3.5 text-right overline">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {partners.map(p => (
                      <tr key={p.id} className="hover:bg-muted/30 transition-colors group">
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary shrink-0">
                              {p.name?.[0]?.toUpperCase()}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-foreground">{p.name}</p>
                              <p className="text-xs text-muted-foreground">{p.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-sm text-muted-foreground hidden lg:table-cell">{p.company || "—"}</td>
                        <td className="px-5 py-4 hidden xl:table-cell">
                          {p.tier_name ? (
                            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                              <span className="w-2 h-2 rounded-full" style={{ background: tierColor(p.tier_id) }} />
                              {p.tier_name}
                            </span>
                          ) : <span className="text-xs text-muted-foreground">—</span>}
                        </td>
                        <td className="px-5 py-4">
                          <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full capitalize ${STATUS_BADGE[p.status] || "bg-muted text-muted-foreground"}`}>
                            {p.status}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-1.5 justify-end">
                            <button onClick={() => openEdit(p)}
                              data-testid={`edit-partner-${p.id}`}
                              title="Edit partner"
                              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all">
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            {p.portal_access ? (
                              <button onClick={e => handleResendCredentials(e, p.id, p.name, p.email)}
                                title="Re-dispatch login credentials"
                                data-testid={`resend-creds-${p.id}`}
                                disabled={dispatchingId === p.id}
                                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-primary/30 text-primary hover:bg-primary/10 transition-all disabled:opacity-50">
                                {dispatchingId === p.id
                                  ? <div className="w-3 h-3 border border-primary border-t-transparent rounded-full animate-spin" />
                                  : <Send className="w-3 h-3" />
                                }
                                {dispatchingId === p.id ? "…" : "Send Creds"}
                              </button>
                            ) : (
                              <button onClick={e => handleDispatchNewPartner(e, p.id, p.name, p.email)}
                                title="Issue portal credentials"
                                data-testid={`issue-creds-${p.id}`}
                                disabled={dispatchingId === p.id}
                                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-amber-500/40 text-amber-600 hover:bg-amber-500/10 transition-all disabled:opacity-50">
                                {dispatchingId === p.id
                                  ? <div className="w-3 h-3 border border-amber-500 border-t-transparent rounded-full animate-spin" />
                                  : <Send className="w-3 h-3" />
                                }
                                {dispatchingId === p.id ? "…" : "Issue Creds"}
                              </button>
                            )}
                            <button onClick={e => { e.stopPropagation(); handleDelete(p.id); }}
                              data-testid={`delete-partner-${p.id}`}
                              title={isSuperAdmin ? "Archive partner" : "Only super admins can delete partners"}
                              disabled={!isSuperAdmin}
                              className={`p-1.5 rounded-lg transition-all ${isSuperAdmin ? "text-muted-foreground hover:text-destructive hover:bg-destructive/10" : "text-muted-foreground/30 cursor-not-allowed"}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                            {isSuperAdmin && (
                              <button onClick={e => { e.stopPropagation(); handlePurge(p.id, p.name); }}
                                data-testid={`purge-partner-${p.id}`}
                                title="Permanently delete all records"
                                className="p-1.5 rounded-lg text-muted-foreground/50 hover:text-red-700 hover:bg-red-500/10 transition-all">
                                <ShieldAlert className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
          {pages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">Page {page} of {pages} · {total} total</p>
              <div className="flex gap-1">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="p-1.5 rounded-full border border-border text-muted-foreground hover:text-foreground disabled:opacity-30 hover:bg-muted transition-colors"><ChevronLeft className="w-4 h-4" /></button>
                <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}
                  className="p-1.5 rounded-full border border-border text-muted-foreground hover:text-foreground disabled:opacity-30 hover:bg-muted transition-colors"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Commissions Tab ───────────────────────────────────────── */}
      {tab === "Commissions" && (
        <>
          <div className="flex items-center gap-2">
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500" />
              Approve/Pay triggers real GL journal entries in platform books
            </p>
            <div className="ml-auto">
              <select value={commStatusFilter} onChange={e => { setCommStatusFilter(e.target.value); setCommPage(1); }}
                className={`${inp} max-w-[150px]`} data-testid="commission-status-filter">
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="paid">Paid</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
          {commLoading ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : commissions.length === 0 ? (
            <div className="bg-card border border-border rounded-2xl flex flex-col items-center py-14 gap-2">
              <DollarSign className="w-9 h-9 text-muted-foreground/25" />
              <p className="text-sm text-muted-foreground">No commissions yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {commissions.map(c => (
                <div key={c.id} className="bg-card border border-border rounded-2xl px-5 py-4 flex items-center gap-4" data-testid={`commission-row-${c.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium">₹{c.amount?.toLocaleString("en-IN")}</p>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${COMM_BADGE[c.status] || "bg-muted text-muted-foreground"}`}>{c.status}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Partner: {c.partner_id?.slice(-6)} {c.description ? `· ${c.description}` : ""}
                    </p>
                    {c.gl_entry_id && (
                      <p className="text-[10px] text-green-600 mt-0.5 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> GL posted · {c.gl_entry_id?.slice(-6)}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {c.status === "pending" && (
                      <button onClick={() => approveCommission(c.id)} data-testid={`approve-commission-${c.id}`}
                        className="text-xs px-3 py-1.5 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                      </button>
                    )}
                    {c.status === "approved" && (
                      <button onClick={() => markPaidCommission(c.id)} data-testid={`markpaid-commission-${c.id}`}
                        className="text-xs px-3 py-1.5 rounded-full bg-green-600 text-white hover:bg-green-700 transition-colors flex items-center gap-1">
                        <DollarSign className="w-3.5 h-3.5" /> Mark Paid
                      </button>
                    )}
                    {c.status === "paid" && (
                      <span className="text-xs text-green-600 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Paid</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {commPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">Page {commPage} of {commPages} · {commTotal} total</p>
              <div className="flex gap-1">
                <button onClick={() => setCommPage(p => Math.max(1, p - 1))} disabled={commPage === 1}
                  className="p-1.5 rounded-full border border-border hover:bg-muted transition-colors disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
                <button onClick={() => setCommPage(p => Math.min(commPages, p + 1))} disabled={commPage === commPages}
                  className="p-1.5 rounded-full border border-border hover:bg-muted transition-colors disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}
        </>
      )}

      {tab === "Pending KYC" && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs text-amber-600">
            <Clock className="w-4 h-4 shrink-0" />
            <span>Partners who self-registered via the public portal and are awaiting KYC verification. Approve to create portal access and email credentials.</span>
          </div>
          {pendingKycLoading ? (
            <div className="flex justify-center py-12">
              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : pendingKyc.length === 0 ? (
            <div className="bg-card border border-border rounded-2xl flex flex-col items-center py-16 gap-2 text-center">
              <CheckCircle2 className="w-9 h-9 text-muted-foreground/25" />
              <p className="text-sm text-muted-foreground">No pending KYC applications</p>
              <a href="/partner/register" target="_blank" className="text-xs text-primary hover:underline">Share registration link</a>
            </div>
          ) : (
            <>
              {/* Bulk action toolbar */}
              <div className="flex items-center justify-between gap-3 px-1">
                <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none"
                  data-testid="select-all-kyc">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded accent-primary cursor-pointer"
                    checked={selectedKyc.size === pendingKyc.length && pendingKyc.length > 0}
                    onChange={e => setSelectedKyc(e.target.checked ? new Set(pendingKyc.map(p => p.id)) : new Set())}
                  />
                  Select All ({pendingKyc.length})
                </label>
                {selectedKyc.size > 0 && (
                  <button
                    onClick={bulkApprove}
                    disabled={bulkApproving}
                    data-testid="bulk-approve-btn"
                    className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-green-600 text-white text-xs font-medium hover:bg-green-700 transition-colors disabled:opacity-60">
                    {bulkApproving
                      ? <><div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" /> Approving…</>
                      : <><CheckCircle2 className="w-3.5 h-3.5" /> Approve Selected ({selectedKyc.size})</>
                    }
                  </button>
                )}
              </div>

              <div className="space-y-3">
                {pendingKyc.map(p => (
                  <div key={p.id}
                    className={`bg-card border rounded-2xl p-4 flex items-start gap-4 transition-colors ${selectedKyc.has(p.id) ? "border-primary/40 bg-primary/5" : "border-border"}`}
                    data-testid={`kyc-row-${p.id}`}>
                    {/* Checkbox */}
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded accent-primary cursor-pointer mt-1 shrink-0"
                      checked={selectedKyc.has(p.id)}
                      onChange={e => {
                        const next = new Set(selectedKyc);
                        e.target.checked ? next.add(p.id) : next.delete(p.id);
                        setSelectedKyc(next);
                      }}
                      data-testid={`kyc-checkbox-${p.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{p.email} · {p.phone}</p>
                      {p.company && <p className="text-xs text-muted-foreground">{p.company}</p>}
                      <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1">
                        {p.pan_number && <span className="text-[10px] font-mono text-muted-foreground">PAN: {p.pan_number}</span>}
                        {p.bank_name && <span className="text-[10px] text-muted-foreground">Bank: {p.bank_name}</span>}
                        {p.bank_ifsc && <span className="text-[10px] font-mono text-muted-foreground">IFSC: {p.bank_ifsc}</span>}
                        {p.state && <span className="text-[10px] text-muted-foreground">{p.state}</span>}
                      </div>
                      {p.created_at && (
                        <p className="text-[10px] text-muted-foreground/60 mt-1">Applied: {fmtDate(p.created_at)}</p>
                      )}
                    </div>
                    <button
                      onClick={() => approveKyc(p.id, p.name, p.email)}
                      data-testid={`approve-kyc-${p.id}`}
                      disabled={dispatchingId === p.id || bulkApproving}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-green-600 text-white text-xs font-medium hover:bg-green-700 transition-colors shrink-0 disabled:opacity-60"
                    >
                      {dispatchingId === p.id
                        ? <><div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" /> Approving…</>
                        : <><CheckCircle2 className="w-3.5 h-3.5" /> Approve & Send</>
                      }
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* RIGHT SHEET — add / edit partner */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md bg-card border-l border-border p-0 flex flex-col">
          <SheetHeader className="px-6 py-5 border-b border-border shrink-0">
            <SheetTitle className="font-heading text-base">{editing ? "Edit Partner" : "Add Partner"}</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name *"><input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className={inp} placeholder="Full name" data-testid="partner-name-input" /></Field>
              <Field label="Email *"><input value={form.email} onChange={e => setForm({...form, email: e.target.value})} className={inp} placeholder="email@example.com" type="email" data-testid="partner-email-input" /></Field>
              <Field label="Phone"><input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className={inp} placeholder="+91 98765 43210" /></Field>
              <Field label="Company"><input value={form.company} onChange={e => setForm({...form, company: e.target.value})} className={inp} placeholder="Company name" /></Field>
              <Field label="Region"><input value={form.region} onChange={e => setForm({...form, region: e.target.value})} className={inp} placeholder="e.g. Maharashtra" /></Field>
              <Field label="Tier">
                <select value={form.tier_id} onChange={e => setForm({...form, tier_id: e.target.value})} className={inp}>
                  <option value="">No Tier</option>
                  {tiers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className={inp}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="pending">Pending</option>
                </select>
              </Field>
            </div>
            <Field label="Notes"><textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className={`${inp} resize-none`} rows={3} placeholder="Optional…" /></Field>
          </div>
          <div className="px-6 py-4 border-t border-border shrink-0 flex gap-3">
            <button onClick={() => setSheetOpen(false)} className="flex-1 px-4 py-2.5 rounded-full border border-border text-sm text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
            <button onClick={handleSubmit} className="flex-1 px-4 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors" data-testid="partner-save-btn">
              {editing ? "Save Changes" : "Add Partner"}
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Commission Sheet */}
      <Sheet open={commSheetOpen} onOpenChange={setCommSheetOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md bg-card border-l border-border p-0 flex flex-col">
          <SheetHeader className="px-6 py-5 border-b border-border shrink-0">
            <SheetTitle className="font-heading text-base">Log Commission</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
            <Field label="Partner">
              <select value={commForm.partner_id} onChange={e => setCommForm({...commForm, partner_id: e.target.value})} className={inp} data-testid="commission-partner-select">
                <option value="">Select partner…</option>
                {partners.map(p => <option key={p.id} value={p.id}>{p.name} ({p.email})</option>)}
              </select>
            </Field>
            <Field label="Amount (₹)">
              <input type="number" value={commForm.amount} onChange={e => setCommForm({...commForm, amount: e.target.value})} className={inp} placeholder="e.g. 5000" data-testid="commission-amount-input" />
            </Field>
            <Field label="Description (optional)">
              <input value={commForm.description || ""} onChange={e => setCommForm({...commForm, description: e.target.value})} className={inp} placeholder="e.g. Q1 referral bonus" />
            </Field>
            <div className="bg-amber-500/10 rounded-xl px-3 py-2 text-xs text-amber-700">
              Approving will auto-post Dr 5900 Referral Commissions / Cr 2100 Trade Payables to platform books.
              Marking Paid will auto-post Dr 2100 Trade Payables / Cr 1100 Bank Operating Account.
            </div>
          </div>
          <div className="px-6 py-4 border-t border-border shrink-0 flex gap-3">
            <button onClick={() => setCommSheetOpen(false)} className="flex-1 px-4 py-2.5 rounded-full border border-border text-sm text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
            <button onClick={saveCommission} disabled={commSaving} className="flex-1 px-4 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-60 transition-colors" data-testid="commission-save-btn">
              {commSaving ? "Saving…" : "Log Commission"}
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
